import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Icon from "components/icons";
import HolidayGiftCards from "components/icons/holidaygiftcards";
import Link from "next/link";
import Button from "components/Button";

const OuterWrapper = styled.section`
  background-color: ${({ theme }) => theme.themeColors.white};
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 5.25rem 1.5rem;
  text-align: center;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize(5)};
  line-height: 1.125;
  letter-spacing: -0.033125rem;
  margin: 0 auto 3rem;
  max-width: 35rem;

  ${({ theme }) => theme.mq.xxl`
    font-size: ${theme.fontSize(6)};
    margin: 0 auto 5rem;
  `}
`;

const SectionDescription = styled.p`
  font-weight: 400;
  line-height: 1.25;
`;

const Grid = styled.div`
  display: grid;
  margin: 0 auto;
  max-width: 62.5rem;

  ${({ theme }) => theme.mq.xxl`
    grid-template-columns: auto auto auto;
    grid-gap: 7rem;
  `}
`;

const GridBlock = styled.div`
  margin: 0 auto 3rem;
`;

const GridBlockTitle = styled.h3`
  margin: 1rem auto 0;
`;

const GridBlockDescription = styled(SectionDescription)`
  margin: 0 auto 0;
`;

const CtaBlock = styled.div`
  display: flex;
  background-color: ${({ theme }) => theme.themeColors.lightGreen1};
  border-radius: 1rem;
  flex-direction: column;
  margin: 3rem auto 0;
  padding: 3rem 1rem 0;
  position: relative;
  width: 100%;
  z-index: 1;

  ${({ theme }) => theme.mq.md`
      margin: 3rem auto 1rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    text-align: left;
    margin: 5rem auto 2rem;
    padding: 3rem 4rem 4rem;
    width: 82.1875rem;
  `}
`;

const CtaTitle = styled(SectionTitle)`
  margin: 0 auto 0.75rem;
  position: relative;
  z-index: 1;

  ${({ theme }) => theme.mq.md`
    br { 
      display: none; 
    }
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0 0 0.75rem;
    max-width: 40%;
  `}
`;

const CtaBlockDescription = styled(SectionDescription)`
  margin: 1rem auto 2rem;
  position: relative;
  z-index: 1;

  ${({ theme }) => theme.mq.xxl`
    margin: 1rem 0 2rem;
  `}
`;

const CtaBlockDescriptionSecondary = styled(SectionDescription)`
  color: ${({ theme }) => theme.themeColors.darkGreen4};
  font-size: ${({ theme }) => theme.fontSize(1)};
  line-height: 1.2;
  margin: 2rem 0 0;

  ${({ theme }) => theme.mq.xxl`
    margin: 1rem 0 2rem;
    position: absolute;
    right: 3rem;
    text-align: right;
    top: 1.25rem;
    z-index: 0;
  `}
`;

const CtaBlockImage = styled(HolidayGiftCards)`
  max-width: calc(100% + 3rem);
  width: auto;
  height: auto;
  margin: 0 -3rem;

  ${({ theme }) => theme.mq.md`
    max-width: 49.75rem;
    width: 100%;
    margin: 0 auto;
    padding: 0 3rem 0 0;
  `}

  ${({ theme }) => theme.mq.xxl`
    padding: 0;
    position: absolute;
    right: 1rem;
    top: -2rem;
    z-index: 0;
  `}
`;

const LinkWrapper = styled.a`
  display: flex;
  margin: 0 auto;
  max-width: 20.3125rem;
  width: 100%;

  ${({ theme }) => theme.mq.md`
    margin: 0 auto 0 auto;
    max-width: 12.5rem;
  `}

  ${({ theme }) => theme.mq.xxl`
    margin: 0;
  `}
`;

function PageTop({
  sectionTitle,
  blockOneTitle,
  blockOneDescription,
  blockTwoTitle,
  blockTwoDescription,
  blockThreeTitle,
  blockThreeDescription,
  ctaBlockTitle,
  ctaBlockDescription,
  ctaBlockDescriptionSecondary,
  buttonLink,
  buttonText,
}) {
  return (
    <OuterWrapper>
      <SectionTitle>{sectionTitle}</SectionTitle>
      <Grid>
        <GridBlock>
          <Icon name="thumbsup-90" />
          <GridBlockTitle>{blockOneTitle}</GridBlockTitle>
          <GridBlockDescription>{blockOneDescription}</GridBlockDescription>
        </GridBlock>
        <GridBlock>
          <Icon name="statistic-90" />
          <GridBlockTitle>{blockTwoTitle}</GridBlockTitle>
          <GridBlockDescription>{blockTwoDescription}</GridBlockDescription>
        </GridBlock>
        <GridBlock>
          <Icon name="calendar-binder-90" />
          <GridBlockTitle>{blockThreeTitle}</GridBlockTitle>
          <GridBlockDescription>{blockThreeDescription}</GridBlockDescription>
        </GridBlock>
      </Grid>
      <CtaBlock>
        <CtaTitle>{ctaBlockTitle}</CtaTitle>
        <CtaBlockDescription>{ctaBlockDescription}</CtaBlockDescription>
        <Link href={buttonLink} passHref>
          <LinkWrapper>
            <Button colorType="primary">{buttonText}</Button>
          </LinkWrapper>
        </Link>
        <CtaBlockDescriptionSecondary>
          {ctaBlockDescriptionSecondary}
        </CtaBlockDescriptionSecondary>
        <CtaBlockImage />
      </CtaBlock>
    </OuterWrapper>
  );
}

export default PageTop;

PageTop.propTypes = {
  sectionTitle: PropTypes.string,
  blockOneTitle: PropTypes.string,
  blockOneDescription: PropTypes.object,
  blockTwoTitle: PropTypes.string,
  blockTwoDescription: PropTypes.object,
  blockThreeTitle: PropTypes.string,
  blockThreeDescription: PropTypes.object,
  ctaBlockTitle: PropTypes.object,
  ctaBlockDescription: PropTypes.object,
  ctaBlockDescriptionSecondary: PropTypes.object,
  buttonLink: PropTypes.string,
  buttonText: PropTypes.string,
};

PageTop.defaultProps = {
  sectionTitle: "",
  blockOneTitle: "",
  blockOneDescription: {},
  blockTwoTitle: "",
  blockTwoDescription: {},
  blockThreeTitle: "",
  blockThreeDescription: {},
  ctaBlockTitle: {},
  ctaBlockDescription: {},
  ctaBlockDescriptionSecondary: {},
  buttonLink: "/",
  buttonText: "Get Started",
};
